import * as Yup from 'yup';
import {
  getBooleanSchema,
  getStringSchema,
  getNumberSchema,
  getSelectSchema,
  getPercentSchema,
} from 'src/compass/shared/schemas';
import { CommissonAdjustmentRadioGroup } from './CommissionAdjustment';

// Custom validation function to check sum of ratePercent in asOfSplits
const validateAsOfSplitsSum = (value: any, context: any) => {
  const parentContext = context.parent;

  const anyNullorNaN = value?.some(
    (item: any) =>
      item.ratePercent === null || Number.isNaN(item.ratePercent) || item.brokerId === null
  );

  if (!parentContext || !parentContext.totalRatePercent || anyNullorNaN) {
    return true;
  }

  const { totalRatePercent } = parentContext;
  const sumOfRatePercent = value.reduce((sum: any, split: any) => sum + split.ratePercent, 0);

  const roundedSum = parseFloat(sumOfRatePercent.toFixed(3));

  if (roundedSum !== totalRatePercent) {
    return false;
  }
  return true;
};

const validateUniqueBrokers = (value: any) => {
  const anyNullorNaN = value?.some((item: any) => item.brokerId === null);
  if (anyNullorNaN) return true;

  const brokerIds = new Set();
  return value
    ?.map((split: { brokerId: any }) => split.brokerId)
    .every((id: any) => {
      if (brokerIds?.has(id)) {
        return false;
      }
      brokerIds.add(id);
      return true;
    });
};

export const AsOfSplitsSchema = Yup.array()
  .test(
    'unique-brokerIds',
    'A broker can not be selected on more than one row.',
    validateUniqueBrokers
  )
  .test(
    'sum-of-ratePercent',
    'Sum of rate percents should be equal to broker rate.',
    validateAsOfSplitsSum
  )
  .of(
    Yup.object().shape({
      ratePercent: getPercentSchema('Rate Percent'),
      brokerId: getSelectSchema('Broker'),
    })
  );

export const CMEScheduleSchema = Yup.object().shape({
  servicingBrokerId: getSelectSchema('Relationship Owner'),
  description: getStringSchema(50, 'Description').nullable(),
  isActive: getBooleanSchema('Active', true),
  asOf: Yup.object({
    clearingRateAmount: getNumberSchema('Clearing (per trade)'),
    totalRatePercent: getPercentSchema('Broker Rate'),
    adjustmentType: Yup.string().required('Adjustment Type selection is required.'),
    year1BrokerRate: Yup.number()
      .nullable()
      .when('adjustmentType', ([adjustmentType], schema) =>
        adjustmentType === CommissonAdjustmentRadioGroup.Year1Adjustments
          ? getPercentSchema('Year1 Broker Rate')
          : schema.transform(() => null)
      ),
    asOfSplits: Yup.array().when('adjustmentType', ([adjustmentType], schema) =>
      adjustmentType === CommissonAdjustmentRadioGroup.SplitWithOtherBrokers
        ? AsOfSplitsSchema
        : schema.default([]).transform(() => [])
    ),
  }),
});
